import { Preloader } from "@smartsuite/react-ui/lib";
import React, { FC, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { FormNotFoundMessage } from "../../components/FormNotFoundMessage/FormNotFoundMessage";
import { PageContentBox } from "../../components/PageContentBox/PageContentBox";
import { FormDetail } from "./FormDetail";
import { useIsPreview } from "../../hooks/useIsPreview";
import { FileStackProvider } from "../../providers/FileStackProvider";
import { useFetchFormConfig } from "./hooks/useFetchFormConfig";
import { useLogoUrl } from "./hooks/useLogoUrl";
import { defineTitle, isFooterVisible } from "./utils";
import { FormPasscodeRequired } from "../../components/FormPasscodeRequired/FormPasscodeRequired";
import { $t } from "../../utils/intl";
import { FormPageText } from "./FormPage.text";
import { PasscodeProvider } from "../../providers/PasscodeProvider";
import { usePasscodeContext } from "../../hooks/usePasscodeContext";

export const FormPageRoute: FC = () => {
  const { accountId, hash } = useParams();
  const { isPreview } = useIsPreview();

  if (!accountId || !hash) {
    return null;
  }

  return <FormPage accountId={accountId} hash={hash} isPreview={isPreview} />;
};

interface FormPageProps {
  accountId: string;
  hash: string;
  isPreview?: boolean;
}

const FormPageContent: FC<FormPageProps> = ({ accountId, hash, isPreview = false }) => {
  const { passcode, setPasscode } = usePasscodeContext();
  const { data, isLoading, statusCode } = useFetchFormConfig({
    accountId,
    hash,
    isPreview,
    passcode,
  });

  const logoUrl = useLogoUrl(data?.form_state?.logo_handle);

  const isNotFound = statusCode === 404;
  const isPasscodeForm = [401, 403].includes(statusCode);

  const isError = useMemo(() => Boolean(passcode) && statusCode !== 200, [statusCode, passcode]);

  useEffect(() => {
    document.title = defineTitle(statusCode, data?.form_state?.tab_title);
  }, [statusCode, data?.form_state?.tab_title]);

  return (
    <PageContentBox
      isPaidPlan={data?.has_active_paid_subscription}
      logoHref={data?.form_state?.logo_href}
      logoUrl={isLoading ? null : logoUrl}
      displayBranding={isFooterVisible(isLoading, data?.display_smartsuite_branding)}
    >
      {isLoading && (
        <div style={{ position: "relative", padding: "50px 0" }}>
          <Preloader />
        </div>
      )}

      {isNotFound && <FormNotFoundMessage />}
      {isPasscodeForm && (
        <FormPasscodeRequired
          title={$t(FormPageText.passcodeTitle)}
          isError={isError}
          onSubmit={(passcode) => setPasscode(passcode)}
        />
      )}

      {data && !isPasscodeForm && (
        <FormDetail
          accountId={accountId}
          hash={hash}
          config={data}
          isPreview={isPreview}
          passcode={passcode}
        />
      )}
    </PageContentBox>
  );
};

const FormFileStackProvider: FC<FormPageProps & { children?: React.ReactNode }> = (props) => {
  const { passcode } = usePasscodeContext();
  const { data } = useFetchFormConfig({ ...props, passcode });

  return (
    <FileStackProvider apiKey={data?.filestack_api_key} security={data?.file_security}>
      <FormPageContent {...props} />
    </FileStackProvider>
  );
};

export const FormPage: FC<FormPageProps> = (props) => {
  return (
    <PasscodeProvider>
      <FormFileStackProvider {...props} />
    </PasscodeProvider>
  );
};
